import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

interface ListItem {
  id: number;
  title: string;
  category: string;
  src: string;
  src2?: string;
  alt: string;
  href?: string;
  comingSoon?: boolean;
}

interface RollingTextItemProps {
  item: ListItem;
}

function RollingTextItem({ item }: RollingTextItemProps) {
  const content = (
    <>
      {/* Rolling text */}
      <div className="relative h-[5.5rem] md:h-[7.5rem] lg:h-[9.5rem] overflow-y-hidden overflow-x-visible flex-1 min-w-0">
        <div className={cn(
          "flex flex-col transition-transform duration-500 ease-out",
          !item.comingSoon && "md:group-hover:-translate-y-1/2"
        )}>
          {/* State 1: Normal */}
          <div className="flex h-[5.5rem] md:h-[7.5rem] lg:h-[9.5rem] items-center">
            <span className={cn(
              "font-bold tracking-tighter leading-none uppercase whitespace-nowrap text-[clamp(3.25rem,11vw,8.75rem)]",
              item.comingSoon ? "text-yacht-cream/40" : "text-yacht-cream"
            )}>
              {item.title}
            </span>
          </div>

          {/* State 2: Hover (Italic + Color) - only if not coming soon, desktop only */}
          {!item.comingSoon && (
            <div className="hidden md:flex h-[5.5rem] md:h-[7.5rem] lg:h-[9.5rem] items-center">
              <span className="font-bold tracking-tighter leading-none italic text-yacht-yellow uppercase whitespace-nowrap text-[clamp(3.25rem,11vw,8.75rem)]">
                {item.title}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Category Label or Coming Soon Tag */}
      {item.comingSoon ? (
        <>
          {/* Mobile: Below text, bright */}
          <span className="md:hidden text-xs uppercase tracking-widest text-yacht-charcoal font-semibold bg-yacht-yellow px-4 py-2 rounded-full mt-2 self-start">
            Coming Soon
          </span>
          {/* Desktop: Absolute positioned */}
          <span className="hidden md:block absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-xs uppercase tracking-widest text-yacht-yellow/80 bg-yacht-yellow/10 px-3 py-1.5 rounded-full border border-yacht-yellow/20">
            Coming Soon
          </span>
        </>
      ) : (
        <span className="hidden md:block absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-sm uppercase tracking-widest text-white/60">
          {item.category}
        </span>
      )}

      {/* Image Reveal Effect - desktop only, only if not coming soon */}
      {!item.comingSoon && (
        <div className="pointer-events-none absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-50 hidden md:block">
          <div className="relative h-48 w-36 md:h-64 md:w-48 lg:h-80 lg:w-60 transition-all duration-500 ease-out opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100">
            <img
              src={item.src}
              alt={item.alt}
              className="absolute inset-0 h-full w-full rounded-lg object-cover shadow-2xl rotate-3"
            />
            {item.src2 && (
              <img
                src={item.src2}
                alt={item.alt}
                className="absolute inset-0 h-full w-full rounded-lg object-cover shadow-2xl -rotate-6 translate-y-8 translate-x-4"
              />
            )}
          </div>
        </div>
      )}
    </>
  );

  const baseClassName = "group relative flex flex-col md:flex-row items-start md:items-center border-b border-white/20 py-6 md:py-12 transition-colors";
  
  // Coming soon items are not clickable
  if (item.comingSoon) {
    return <div className={cn(baseClassName, "cursor-default")}>{content}</div>;
  }

  if (item.href) {
    return (
      <Link to={item.href} className={cn(baseClassName, "cursor-pointer")}>
        {content}
      </Link>
    );
  }

  return <div className={cn(baseClassName, "cursor-pointer")}>{content}</div>;
}

interface RollingTextListProps {
  items: ListItem[];
}

function RollingTextList({ items }: RollingTextListProps) {
  return (
    <div className="flex flex-col">
      {items.map((item) => (
        <RollingTextItem key={item.id} item={item} />
      ))}
    </div>
  );
}

export { RollingTextList, RollingTextItem };
export type { ListItem };
